import { ACL_TYPE } from '@openx/types/aclType.ts';
import type { InstanceProps } from '@openx/types/targeting/inventoryAndContent.ts';
import { useIsAllowed } from '@openx/utils/permission/useIsAllowed.ts';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { searchInventoriesInstances } from '@openx/utils/state/packages/inventories/api.ts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTargetingContext } from '../../context/TargetingContext.tsx';

const filterInstancesByMarkets = (instances, market) =>
  market ? instances.filter(({ market_operator_id }) => market_operator_id === market) : instances;

export const useFetchInventoryInstances = (marketId: null | string = null) => {
  const [instances, setInstances] = useState<InstanceProps[] | undefined>([]);
  const { isAllowed } = useIsAllowed();

  const isUserAllowed =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE));

  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();

  const { data } = useQuery<InstanceProps[] | null>({
    queryFn: async () => {
      if (isUserAllowed && isBefInstance) {
        const response = await searchInventoriesInstances();

        return filterInstancesByMarkets(response, marketId);
      }

      return [];
    },
    queryKey: [marketId, isUserAllowed, isBefInstance],
  });

  useEffect(() => {
    setInstances(data ?? []);
  }, [data]);

  return {
    instances,
  };
};
